import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { API_URL, ENVIRONMENT } from 'settings';

export const IDLE_MS = 15 * 60 * 1000;
export const REPORT_MS = 60 * 1000;
export const WARNING_MS = 45 * 1000;
const prefix = `pm_${ENVIRONMENT}_presence`;
type Context = { session_id?: number; person_id?: number };
type Login = {
  presence_id: string;
  presence_started_at: number;
  presence_idle_seconds?: number;
};
type Shared = {
  activity: number;
  acknowledged: number;
  contextVersion: number;
  acknowledgedContext: number;
  nextSend: number;
  reported: number;
  deadline: number;
  contexts: Record<string, Context & { at: number }>;
};
export type ActivityClaim = {
  sid: string;
  activity: number;
  contextVersion: number;
  marker: string;
};
let context: Context = {};
let tab = '';
let wake: (() => void) | undefined;
let expire: (() => void) | undefined;
let lastLocalActivity = 0;
const loginUnavailable = () => new Error(
  'Could not start a login session. Enable site storage and reload, or use an up-to-date browser.',
);

const pending = (state: Shared) =>
  state.activity > state.acknowledged ||
  (state.contextVersion || 0) > (state.acknowledgedContext || 0);

export function browserLogin(token?: string) {
  try {
    token =
      token === undefined ? Cookies.get(`pm_${ENVIRONMENT}_token`) : token;
    const login = token ? jwtDecode<Login>(token) : undefined;
    return login?.presence_id ? login : undefined;
  } catch {
    return undefined;
  }
}

function idleMs(login: Login) {
  const seconds = login.presence_idle_seconds;
  return typeof seconds === 'number' &&
    Number.isFinite(seconds) &&
    seconds >= 60
    ? seconds * 1000
    : IDLE_MS;
}

function read(login: Login): Shared {
  const raw = localStorage.getItem(`${prefix}:${login.presence_id}`);
  return raw
    ? JSON.parse(raw)
    : {
        activity: login.presence_started_at * 1000,
        acknowledged: 0,
        contextVersion: 0,
        acknowledgedContext: 0,
        nextSend: login.presence_started_at * 1000 + REPORT_MS,
        reported: login.presence_started_at,
        deadline: login.presence_started_at * 1000 + idleMs(login),
        contexts: {},
      };
}

function write(sid: string, state: Shared) {
  localStorage.setItem(`${prefix}:${sid}`, JSON.stringify(state));
  wake?.();
}

function trimContexts(state: Shared, now: number, idle: number) {
  state.contexts = Object.fromEntries(
    Object.entries(state.contexts)
      .filter(([, value]) => now - value.at < idle)
      .sort((a, b) => b[1].at - a[1].at)
      .slice(0, 8),
  );
}

export async function browserId(): Promise<string> {
  if (!navigator.locks) throw loginUnavailable();
  try {
    return await navigator.locks.request(`${prefix}:identity`, () => {
      let id = localStorage.getItem(`${prefix}:browser`);
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(`${prefix}:browser`, id);
      }
      return id;
    });
  } catch {
    throw loginUnavailable();
  }
}

export function beginPresenceLogin(
  access: string,
  server?: { expires_at: number; server_at: number },
) {
  const login = browserLogin(access);
  if (!login || !server || !Number.isFinite(server.expires_at) ||
      !Number.isFinite(server.server_at) || server.expires_at <= server.server_at)
    throw loginUnavailable();
  try {
    const now = Date.now();
    write(login.presence_id, {
      activity: now,
      acknowledged: 0,
      contextVersion: 0,
      acknowledgedContext: 0,
      nextSend: now + REPORT_MS + Math.floor(Math.random() * 5000),
      reported: server.server_at,
      deadline: now + (server.expires_at - server.server_at) * 1000,
      contexts: {},
    });
  } catch {
    throw loginUnavailable();
  }
}

export function setPresenceContext(next: Context) {
  context =
    next.session_id === context.session_id ? { ...context, ...next } : next;
  updateContext();
}

export function setPresencePerson(person_id?: number, session_id?: number) {
  if (session_id && context.session_id !== session_id) return;
  context = { ...context, person_id };
  updateContext();
}

function updateContext() {
  const login = browserLogin();
  if (!login || !navigator.locks) return;
  void navigator.locks
    .request(`${prefix}:${login.presence_id}`, () => {
      if (browserLogin()?.presence_id !== login.presence_id) return;
      const state = read(login);
      if (
        Date.now() >= Math.min(state.activity + idleMs(login), state.deadline)
      )
        return;
      tab ||= crypto.randomUUID();
      const previous = state.contexts[tab];
      if (
        previous &&
        previous.session_id === context.session_id &&
        previous.person_id === context.person_id
      )
        return;
      // Context changes do not count as human activity or extend the idle deadline.
      state.contexts[tab] = { ...context, at: state.activity };
      trimContexts(state, Date.now(), idleMs(login));
      state.contextVersion = (state.contextVersion || 0) + 1;
      write(login.presence_id, state);
    })
    .catch(() => undefined);
}

export function notePresenceActivity(event: Event) {
  if (!event.isTrusted || document.visibilityState !== 'visible') return;
  if (Date.now() - lastLocalActivity < 1000) return;
  lastLocalActivity = Date.now();
  const login = browserLogin();
  if (!login || !navigator.locks) return;
  void navigator.locks
    .request(`${prefix}:${login.presence_id}`, () => {
      if (browserLogin()?.presence_id !== login.presence_id) return;
      const state = read(login);
      const now = Date.now();
      if (now >= Math.min(state.activity + idleMs(login), state.deadline)) {
        expire?.();
        return;
      }
      state.activity = Math.max(state.activity, now);
      tab ||= crypto.randomUUID();
      state.contexts[tab] = { ...context, at: now };
      trimContexts(state, now, idleMs(login));
      write(login.presence_id, state);
    })
    .catch(() => undefined);
}

export async function claimActivity(
  token?: string,
): Promise<ActivityClaim | undefined> {
  const login = browserLogin(token);
  if (!login || !navigator.locks) return undefined;
  try {
    const latest = read(login);
    if (!pending(latest) || Date.now() < latest.nextSend) return undefined;
    return await navigator.locks.request(
      `${prefix}:${login.presence_id}`,
      { ifAvailable: true },
      lock => {
        if (!lock || browserLogin()?.presence_id !== login.presence_id) return;
        const state = read(login);
        const now = Date.now();
        if (now >= Math.min(state.activity + idleMs(login), state.deadline))
          return;
        if (!pending(state) || now < state.nextSend) return;
        // Failed/aborted requests consume the same allowance. Jitter is additive.
        state.nextSend = now + REPORT_MS + Math.floor(Math.random() * 5000);
        const contexts = Object.values(state.contexts)
          .filter(value => now - value.at < idleMs(login))
          .slice(0, 8)
          .map(({ at, ...value }) => value);
        if (!contexts.length && Object.keys(context).length)
          contexts.push(context);
        write(login.presence_id, state);
        return {
          sid: login.presence_id,
          activity: state.activity,
          contextVersion: state.contextVersion || 0,
          marker: JSON.stringify({ age_ms: now - state.activity, contexts }),
        };
      },
    );
  } catch {
    return undefined;
  }
}

export function acceptPresenceResponse(
  response: Response,
  token?: string,
  claim?: ActivityClaim,
) {
  const login = browserLogin(token);
  if (
    !login ||
    browserLogin()?.presence_id !== login.presence_id ||
    !navigator.locks
  )
    return;
  const raw = response.headers?.get('X-PM-Presence');
  if (!raw) return;
  let received: { reported_at: number; expires_at: number; server_at: number };
  try {
    received = JSON.parse(raw);
    // Ordinary polling responses normally repeat the same lease. Avoid a lock,
    // storage write and cross-tab wake-up for each of those responses.
    if (
      !Number.isFinite(received.reported_at) ||
      received.reported_at <= read(login).reported
    )
      return;
  } catch {
    return;
  }
  void navigator.locks
    .request(`${prefix}:${login.presence_id}`, () => {
      if (browserLogin()?.presence_id !== login.presence_id) return;
      const state = read(login);
      if (received.reported_at <= state.reported) return;
      if (claim && received.reported_at > state.reported) {
        state.acknowledged = Math.max(state.acknowledged, claim.activity);
        state.acknowledgedContext = Math.max(
          state.acknowledgedContext || 0,
          claim.contextVersion,
        );
      }
      state.reported = received.reported_at;
      state.deadline =
        Date.now() + (received.expires_at - received.server_at) * 1000;
      write(login.presence_id, state);
    })
    .catch(() => undefined);
}

export function presenceRejected(token?: string) {
  if (
    browserLogin(token)?.presence_id === browserLogin()?.presence_id &&
    browserLogin(token)
  ) {
    expire?.();
  }
}

export function notifyPresenceLogout(refresh?: string) {
  // Capture the old refresh credential before cookies are cleared. The backend
  // binds it to that login, so a delayed logout cannot end a newer login.
  try {
    if (!refresh) {
      if (!browserLogin()) return;
      refresh = Cookies.get(`pm_${ENVIRONMENT}_refresh`);
    }
    if (!refresh) return;
    void fetch(`${API_URL}/presence/logout/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {}
}

export function startPresence(
  onExpired: () => void,
  onWarning?: (deadline: number | undefined) => void,
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  let warning: number | undefined;
  const warn = (deadline?: number) => {
    if (warning === deadline) return;
    warning = deadline;
    onWarning?.(deadline);
  };
  lastLocalActivity = 0;
  expire = () => {
    if (stopped) return;
    stopped = true;
    if (timer) clearTimeout(timer);
    warn();
    onExpired();
  };
  const schedule = () => {
    if (timer) clearTimeout(timer);
    if (stopped) return;
    const login = browserLogin();
    if (!login) {
      warn();
      return;
    }
    try {
      const state = read(login);
      const deadline = Math.min(state.activity + idleMs(login), state.deadline);
      const now = Date.now();
      const warningAt = deadline - WARNING_MS;
      warn(
        document.visibilityState === 'visible' &&
          now >= warningAt &&
          now < deadline
          ? deadline
          : undefined,
      );
      const due = pending(state) ? state.nextSend : deadline;
      // One extra local wake-up at the warning boundary, not another heartbeat.
      const nextWarning = now < warningAt ? warningAt : deadline;
      timer = setTimeout(
        tick,
        Math.max(100, Math.min(deadline, due, nextWarning) - now),
      );
    } catch {}
  };
  const tick = async () => {
    const login = browserLogin();
    if (!login || stopped) return;
    try {
      const state = read(login);
      if (
        Date.now() >= Math.min(state.activity + idleMs(login), state.deadline)
      ) {
        expire?.();
        return;
      }
      if (pending(state) && Date.now() >= state.nextSend) {
        const claim = await claimActivity(
          Cookies.get(`pm_${ENVIRONMENT}_token`),
        );
        if (!claim) return;
        // Token refresh stays on its existing path and never renews presence.
        const { getValidAccessToken } = await import(
          'services/Api/refreshHelper'
        );
        const token = await getValidAccessToken();
        if (stopped || browserLogin(token)?.presence_id !== login.presence_id)
          return;
        if (claim) {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 5000);
          try {
            const response = await fetch(`${API_URL}/presence/activity/`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-PM-Activity': claim.marker,
              },
              signal: controller.signal,
            });
            acceptPresenceResponse(response, token, claim);
            if (response.status === 401) presenceRejected(token);
          } finally {
            clearTimeout(timeout);
          }
        }
      }
    } catch {
      // No tight retry. The shared allowance remains consumed on failure.
    } finally {
      schedule();
    }
  };
  wake = schedule;
  const storage = (event: StorageEvent) => {
    if (event.key?.startsWith(prefix)) schedule();
  };
  document.addEventListener('pointerdown', notePresenceActivity, {
    passive: true,
  });
  document.addEventListener('keydown', notePresenceActivity, { passive: true });
  window.addEventListener('storage', storage);
  window.addEventListener('focus', schedule);
  document.addEventListener('visibilitychange', schedule);
  schedule();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    warn();
    wake = undefined;
    expire = undefined;
    document.removeEventListener('pointerdown', notePresenceActivity);
    document.removeEventListener('keydown', notePresenceActivity);
    window.removeEventListener('storage', storage);
    window.removeEventListener('focus', schedule);
    document.removeEventListener('visibilitychange', schedule);
  };
}
